import { useEffect, useState } from 'react';
import { GitCommit } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { ApiResponse } from '../types';

const REPO_URL = 'https://github.com/ParadiseMediaOrg/domainhq';
const WEB_COMMIT = __APP_COMMIT__;
const short = (sha: string) => (/^[0-9a-f]{7,40}$/i.test(sha) ? sha.slice(0, 7) : sha);

interface ApiVersion {
  commit: string;
  started_at: string;
}

/**
 * Which commits are deployed: the web bundle's (baked in at build time) and
 * the API's (from /api/version). They are separate Railway services, so they
 * can drift during or after a deploy — a mismatch is shown in amber.
 */
export default function VersionTag({ collapsed }: { collapsed?: boolean }) {
  const { fetchApi } = useApi();
  const [api, setApi] = useState<ApiVersion | null>(null);

  useEffect(() => {
    fetchApi<ApiResponse<ApiVersion>>('/version')
      .then((res) => { if (res.data) setApi(res.data); })
      .catch(() => { /* older API without /version, or offline */ });
  }, [fetchApi]);

  const mismatch = !!api && api.commit !== 'unknown' && WEB_COMMIT !== 'unknown' && api.commit !== WEB_COMMIT;
  const title = [
    `Web ${WEB_COMMIT} (built ${formatDistanceToNow(new Date(__APP_BUILT_AT__), { addSuffix: true })})`,
    api ? `API ${api.commit} (started ${formatDistanceToNow(new Date(api.started_at), { addSuffix: true })})` : 'API version unavailable',
    mismatch ? 'Web and API are running different commits.' : '',
  ].filter(Boolean).join('\n');
  const tone = mismatch ? 'text-[#fbbf24] hover:text-[#fcd34d]' : 'text-ink-500 hover:text-ink-300';
  const link = (sha: string) => (/^[0-9a-f]{7,40}$/i.test(sha) ? `${REPO_URL}/commit/${sha}` : undefined);

  if (collapsed) {
    return (
      <a href={link(WEB_COMMIT)} target="_blank" rel="noopener noreferrer" title={title}
        className={`flex justify-center py-1.5 ${tone}`}>
        <GitCommit className="h-4 w-4" />
      </a>
    );
  }

  return (
    <div title={title} className={`flex items-center gap-1.5 px-3 py-1 text-[11px] font-mono ${mismatch ? 'text-[#fbbf24]' : 'text-ink-500'}`}>
      <GitCommit className="h-3.5 w-3.5 shrink-0" />
      {api && api.commit !== WEB_COMMIT ? (
        <>
          <span>web</span>
          <a href={link(WEB_COMMIT)} target="_blank" rel="noopener noreferrer" className={tone}>{short(WEB_COMMIT)}</a>
          <span>· api</span>
          <a href={link(api.commit)} target="_blank" rel="noopener noreferrer" className={tone}>{short(api.commit)}</a>
        </>
      ) : (
        <a href={link(WEB_COMMIT)} target="_blank" rel="noopener noreferrer" className={tone}>{short(WEB_COMMIT)}</a>
      )}
    </div>
  );
}
